import { shade } from '../pixel.js';
import type { PixelCanvas } from '../pixel.js';
import { box, drawBox, drawCylinder, faces, frontFace, iso } from './iso.js';
import type { FurniDrawer } from './iso.js';

/** Drawers for the blindbox prizes (see protocol furniture/prizes.ts). */

const GOLD = '#f0bf36';
const GOLD_DARK = '#b98a1e';

function drawLantern(pc: PixelCanvas): void {
  drawCylinder(pc, 0.06, 0, 30, '#6a4a2a', '#4a3320');
  drawCylinder(pc, 0.2, 30, 34, GOLD, GOLD_DARK);
  drawCylinder(pc, 0.26, 34, 52, '#ff8a70', '#d9433b');
  drawCylinder(pc, 0.2, 52, 56, GOLD, GOLD_DARK);
  const c = iso(0, 0, 44);
  pc.rect(c.x - 2, c.y - 6, 4, 8, '#ffd9a0');
  pc.rect(c.x - 1, c.y + 2, 2, 5, GOLD);
}

function drawPlushBear(pc: PixelCanvas, rotation: number): void {
  const fur = '#c98a55';
  drawBox(pc, box(-0.26, 0.26, -0.22, 0.22, 0, 26, faces(fur)));
  drawBox(pc, box(-0.2, 0.2, -0.2, 0.2, 26, 44, faces(fur)));
  drawBox(pc, box(0.1, 0.3, -0.1, 0.1, 30, 38, faces('#f0d3a8')));
  const head = iso(0, 0, 44);
  pc.rect(head.x - 9, head.y - 3, 4, 4, fur);
  pc.rect(head.x + 6, head.y - 3, 4, 4, shade(fur, 0.8));
  const face = iso(0.16, 0.12, 36);
  pc.px(face.x - 3, face.y, '#2a1e19');
  pc.px(face.x + 3, face.y, '#2a1e19');
  pc.rect(face.x - 1, face.y + 2, 3, 2, '#2a1e19');
  const bow = iso(0.1, 0, 27);
  pc.rect(bow.x - 5, bow.y - 2, 4, 4, '#d9433b');
  pc.rect(bow.x + 2, bow.y - 2, 4, 4, '#d9433b');
  if (frontFace(rotation) === 'right') pc.flipX();
}

function drawNeonSign(pc: PixelCanvas, rotation: number): void {
  drawBox(pc, box(-0.4, 0.4, -0.06, 0.06, 6, 50, faces('#2a2630')));
  drawBox(pc, box(-0.36, 0.36, -0.2, 0.2, 0, 6, faces('#4a4652')));
  const c = iso(0, 0.06, 28);
  pc.rect(c.x - 12, c.y - 12, 24, 3, '#ff5fa2');
  pc.rect(c.x - 12, c.y + 8, 24, 3, '#ff5fa2');
  pc.rect(c.x - 12, c.y - 12, 3, 23, '#ff5fa2');
  pc.rect(c.x + 9, c.y - 12, 3, 23, '#ff5fa2');
  pc.rect(c.x - 6, c.y - 4, 12, 3, '#7fe0ff');
  pc.rect(c.x - 6, c.y + 2, 9, 3, '#7fe0ff');
  if (frontFace(rotation) === 'right') pc.flipX();
}

function drawLavaLamp(pc: PixelCanvas): void {
  drawCylinder(pc, 0.2, 0, 8, '#5a5a66', '#3a3a44');
  drawCylinder(pc, 0.14, 8, 34, '#ff9a4a', '#e8622a');
  drawCylinder(pc, 0.08, 34, 40, '#5a5a66', '#3a3a44');
  const c = iso(0, 0, 20);
  pc.ellipse(c.x - 2, c.y, 3, 4, '#ffd166');
  pc.ellipse(c.x + 2, c.y - 8, 2, 3, '#ffd166');
  pc.ellipse(c.x - 1, c.y + 6, 2, 2, '#ffd166');
}

function drawTrophy(pc: PixelCanvas): void {
  drawBox(pc, box(-0.28, 0.28, -0.28, 0.28, 0, 8, faces('#4a3a2a', '#6a5238')));
  drawCylinder(pc, 0.1, 8, 26, '#ffe27a', GOLD_DARK);
  drawCylinder(pc, 0.3, 26, 46, '#ffe27a', GOLD);
  const c = iso(0, 0, 36);
  pc.rect(c.x - 15, c.y - 6, 3, 8, GOLD_DARK);
  pc.rect(c.x + 13, c.y - 6, 3, 8, GOLD_DARK);
  pc.rect(c.x - 2, c.y - 8, 4, 8, '#fff2b0');
}

function drawCrystalBall(pc: PixelCanvas): void {
  drawCylinder(pc, 0.26, 0, 8, '#8a5cc0', '#5a3a86');
  drawCylinder(pc, 0.2, 8, 12, '#a87ee0', '#7a4cb0');
  const c = iso(0, 0, 26);
  pc.ellipse(c.x, c.y, 12, 12, '#7a5cc8');
  pc.ellipse(c.x, c.y - 1, 10, 10, '#b8a0f0');
  pc.ellipse(c.x - 3, c.y - 4, 3, 3, '#f4eeff');
}

function drawPhoenix(pc: PixelCanvas, rotation: number): void {
  const red = '#d9432b';
  const orange = '#ff9a2a';
  drawBox(pc, box(-0.3, 0.3, -0.3, 0.3, 0, 8, faces('#5a4a3a', '#7a6650')));
  drawBox(pc, box(-0.12, 0.12, -0.12, 0.12, 8, 34, faces(red)));
  drawBox(pc, box(-0.08, 0.18, -0.08, 0.08, 34, 46, faces(orange)));
  drawBox(pc, box(-0.36, -0.04, -0.06, 0.06, 20, 36, faces(orange)));
  drawBox(pc, box(-0.04, 0.24, -0.4, -0.08, 20, 36, faces(red)));
  const head = iso(0.1, 0, 46);
  pc.px(head.x + 2, head.y - 3, '#1a1216');
  pc.rect(head.x + 4, head.y - 1, 4, 2, GOLD);
  if (frontFace(rotation) === 'right') pc.flipX();
}

function drawRainbowFountain(pc: PixelCanvas): void {
  drawCylinder(pc, 0.46, 0, 10, '#a7d8ff', '#8a909c');
  drawCylinder(pc, 0.4, 10, 14, '#ff7ab8', '#e0568f');
  drawCylinder(pc, 0.3, 14, 18, '#ffd166', '#e0a83a');
  drawCylinder(pc, 0.12, 18, 40, '#e6f7ff', '#a9b0b7');
  drawCylinder(pc, 0.22, 40, 44, '#7fe0ff', '#4aa8d8');
}

export const PRIZE_DRAWERS: Record<string, FurniDrawer> = {
  prize_lantern: drawLantern,
  prize_plush_bear: drawPlushBear,
  prize_neon_sign: drawNeonSign,
  prize_lava_lamp: drawLavaLamp,
  prize_trophy: drawTrophy,
  prize_crystal_ball: drawCrystalBall,
  prize_phoenix: drawPhoenix,
  prize_rainbow_fountain: drawRainbowFountain,
};
